import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {AppButton} from '../../../components/Button';
import ConnectionTrackButton from '../../../components/Button/ConnectionTrackButton/ConnectionTrackButton';
import {ErrorModalTemplate} from '../../../components/DataDisplay';
import appConfig from '../../../config/appConfig';
import * as localizationKeys from '../../../consts/localization/localizationKeys';
import FWU_TARGET_TYPES from '../../../enums/iot/fwuTargetTypes';
import useLoader from '../../../hooks/useLoader';
import {amplitudeFwuTracking} from '../../../services/amplitude';
import connectivityService from '../../../services/device/connectivityService';
import fwuService from '../../../services/device/fwuService';
import fwuTargetService from '../../../services/device/fwuTargetService';
import fwuPackageService from '../../../services/fwu/fwuPackageService';
import uiMessageRequestService from '../../../services/iot/uiMessageRequestService';
import {getLocalizedStrings} from '../../../services/localization/localizationService';
import modalService from '../../../services/modalService';
import {hideAllModalsAction} from '../../../state/ducks/modal';
import {selectIsStartAutoSync} from '../../../state/selectors/global';
import {
    selectIsDeviceDisconnected,
    selectIsDevicePaired,
    selectIsHolderConnected,
} from '../../../state/selectors/iotDevice';

const DeviceInSblModePopup = (props) => {
    const {modalType, deviceType, fwu_target} = props;
    const dispatch = useDispatch();
    const isBle = connectivityService.isBle();
    const isFwuEnabled = isBle
        ? appConfig.getIsBleFWUAvailable(deviceType)
        : appConfig.getIsUsbFWUAvailable(deviceType);
    const isDeviceDisconnected = useSelector(selectIsDeviceDisconnected);
    const isDevicePaired = useSelector(selectIsDevicePaired);
    const isStartAutoSync = useSelector(selectIsStartAutoSync);
    const localizedStrings = getLocalizedStrings();
    const isFwuTargetHolderRelated = fwuTargetService.isFwuTargetHolderRelated(fwu_target);
    const isHolderConnected = useSelector(selectIsHolderConnected);
    const doesDeviceSupportSblUpdate = fwuPackageService.checkIfDeviceSupportsSblUpdate(fwu_target);
    const isFwuTargetRequiresUsbConnection = fwuTargetService.isFwuTargetRequiresUsbConnection(
        fwu_target,
        doesDeviceSupportSblUpdate
    );
    const isUsbConnectionRequired = isBle && isFwuTargetRequiresUsbConnection;
    const isFwuAvailable = isFwuEnabled && !isUsbConnectionRequired;
    const hideModal = () => modalService.hideModal(modalType);
    const {hideLoader, showLoader} = useLoader('DEVICE_IN_SBL_CHECK_FWU_AVAILABILITY_LOADER');

    const onClick = async () => {
        const {isCheckInProgress, isAvailable} = fwuPackageService.getFwuPackageCheckInfo(fwu_target);
        const initiateFwu = () => {
            const availableFwuPackages = fwuPackageService.getFwuPackagesStatic();

            dispatch(hideAllModalsAction());

            if (!availableFwuPackages.length) return;

            fwuService.initiateUpdateFirmwareMessage(availableFwuPackages[0].target);
        };

        amplitudeFwuTracking.trackUpgradeStartedFromDeviceInSbl(isFwuTargetHolderRelated);

        if (!isFwuAvailable) {
            hideModal();
            return;
        } else {
            // if firmwareAvailability request is in progress or package is not available
            // show loader and request firmwareAvailability
            if (isCheckInProgress || (!isCheckInProgress && !isAvailable)) {
                try {
                    showLoader();

                    // If target is holder -> check fwu for charger as well, because charger should be updated at first
                    const requests = isFwuTargetHolderRelated
                        ? [
                              uiMessageRequestService().publishCheckFirmwareMessage(FWU_TARGET_TYPES.CHARGER_OR_MONO),
                              uiMessageRequestService().publishCheckFirmwareMessage(fwu_target),
                          ]
                        : [uiMessageRequestService().publishCheckFirmwareMessage(fwu_target)];

                    await Promise.all(requests);
                } finally {
                    hideLoader();
                    // forward to fwu page, if firmwareAvailability is failed - fwuError popup will be displayed
                    initiateFwu();
                }
            } else {
                hideModal();

                initiateFwu();
            }
        }
    };
    const onClose = () => hideModal();

    useEffect(() => {
        if (isDeviceDisconnected || (isFwuTargetHolderRelated && !isHolderConnected) || isDevicePaired) {
            hideModal();
        }
    }, [isDeviceDisconnected, isHolderConnected, isDevicePaired]);

    const titleKey = isFwuTargetHolderRelated
        ? localizationKeys.HOLDER_IN_SBL_USB_UPDATE_POPUP_TITLE_TEXT
        : localizationKeys.CHARGER_IN_SBL_USB_UPDATE_POPUP_TITLE_TEXT;
    const descriptionKey = isFwuTargetHolderRelated
        ? localizationKeys.HOLDER_IN_SBL_USB_UPDATE_POPUP_MESSAGE_TEXT
        : localizationKeys.CHARGER_IN_SBL_USB_UPDATE_POPUP_MESSAGE_TEXT;

    let btnKey = localizationKeys.CHARGER_IN_SBL_USB_UPDATE_POPUP_UPDATE_BUTTON_TEXT;

    if (!isFwuAvailable) {
        btnKey = localizationKeys.HOLDER_IN_SBL_BLE_UPDATE_POPUP_OK_BUTTON_TEXT;
    } else if (isFwuTargetHolderRelated) {
        btnKey = localizationKeys.HOLDER_IN_SBL_USB_UPDATE_POPUP_UPDATE_BUTTON_TEXT;
    }

    return isStartAutoSync ? (
        <ErrorModalTemplate
            titleKey={titleKey}
            descriptionKey={descriptionKey}
            onClose={isFwuAvailable ? undefined : onClose}
        >
            <ConnectionTrackButton button={AppButton} primary light onClick={onClick}>
                {localizedStrings[btnKey]}
            </ConnectionTrackButton>
        </ErrorModalTemplate>
    ) : null;
};

export default DeviceInSblModePopup;
